import { useMemo, useState } from 'react';
import useCustomerIO from '@/hooks/useCustomerIO';
import {
  sectionList,
  sectionState,
  UPDATES_SECTIONS,
  type UpdatesSection,
} from '@/hooks/useOrderUpdates';
import type { OrderUpdates } from '@/types/orderUpdates';
import {
  baghdadDayKey,
  collapseCallRuns,
  groupByDay,
  mergeTimeline,
  type TimelineLists,
} from './timeline';

export type UpdatesFilter = 'all' | UpdatesSection;
export const TIMELINE_PAGE = 10;

/** A down section contributes no rows; its notice says why instead. */
const readyLists = (data: OrderUpdates | undefined): TimelineLists => ({
  // BOXY-587 — the merchant sees Boxy's outgoing calls only, never an incoming one.
  calls:
    sectionState(data, 'calls', false) === 'ready'
      ? (sectionList(data, 'calls') ?? []).filter(
          (call) => (call.direction ?? 'outgoing') === 'outgoing',
        )
      : [],
  comments:
    sectionState(data, 'comments', false) === 'ready' ? (sectionList(data, 'comments') ?? []) : [],
  whatsapp:
    sectionState(data, 'whatsapp', false) === 'ready' ? (sectionList(data, 'whatsapp') ?? []) : [],
});

/**
 * The unified Updates timeline: merge the three sources newest first, filter by
 * chip, show the newest page, group the shown rows by day.
 */
const useUpdatesTimeline = (data: OrderUpdates | undefined) => {
  const { trackEvent } = useCustomerIO();
  const [filter, setFilterState] = useState<UpdatesFilter>('all');
  const [expanded, setExpanded] = useState(false);

  const lists = useMemo(() => readyLists(data), [data]);
  const merged = useMemo(() => mergeTimeline(lists), [lists]);
  const lastCall = merged.find((item) => item.kind === 'calls');
  const down = data
    ? UPDATES_SECTIONS.filter((key) => sectionState(data, key, false) === 'unavailable')
    : [];

  const filtered = collapseCallRuns(
    filter === 'all' ? merged : merged.filter((item) => item.kind === filter),
  );
  const visible = expanded ? filtered : filtered.slice(0, TIMELINE_PAGE);

  const setFilter = (next: UpdatesFilter) => {
    if (next === filter) return;
    trackEvent('updates_filter_changed', { filter: next });
    setFilterState(next);
    setExpanded(false);
  };

  const showEarlier = () => {
    trackEvent('updates_show_all_clicked', { section: filter });
    setExpanded(true);
  };

  return {
    filter,
    setFilter,
    counts: {
      calls: lists.calls.length,
      comments: lists.comments.length,
      whatsapp: lists.whatsapp.length,
    },
    lastCall: lastCall?.kind === 'calls' ? lastCall.call : undefined,
    // BOXY-715 renders the three sources as their own sections, newest first.
    calls: [...lists.calls].sort((a, b) => String(b.at).localeCompare(String(a.at))),
    comments: [...lists.comments].sort((a, b) => String(b.at).localeCompare(String(a.at))),
    answered: lists.calls.filter((call) => call.outcome === 'answered').length,
    whatsapp: lists.whatsapp,
    down,
    days: groupByDay(visible, baghdadDayKey(Date.now())),
    shownCount: visible.length,
    earlierCount: filtered.length - visible.length,
    showEarlier,
    truncated: data?.truncated === true,
  };
};

export type UpdatesTimeline = ReturnType<typeof useUpdatesTimeline>;
export default useUpdatesTimeline;
